import { ActivatedRoute } from '@angular/router';
import { ChangeDetectionStrategy, Component, Self } from '@angular/core';
import { SponsorSelectedSubjectLabResultsStorage } from './sponsor-selected-subject-lab-results-storage';
import { GridLegendItem } from '../../legend/grid-legend.component';
import { SubjectTab } from '../../subjects/subject-tab';
import { FormControl } from '@angular/forms';
import { Subscription, combineLatest, Observable } from 'rxjs';
import { SponsorStudy } from '../../sponsor-dashboard/sponsor-study';
import { UrlBuilder } from '../../core/url-builder/url-builder';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';
import { SponsorRouteData } from '../sponsor-route-data';
import { chain, isEmpty } from 'lodash';
import { SUBJECT_LAB_TESTS_DATA_SOURCE } from '../../subjects/subject-lab-tests-data-source';
import { DataRevisionIndicator } from '../../shared';
import { map, refCount, publishReplay, filter } from 'rxjs/operators';
import { SponsorCtSubjectsStorage } from './sponsor-ct-subjects-storage';
import { Subject } from '../../subjects/Subject';
import { SponsorSubjectIdentity } from '../sponsor-subject-item';
import { routeParams, routeData } from '../../shared/route-helpers';
import { SponsorSelectedSubjectRouteData } from './sponsor-selected-subject-route-data';

class SubjectLookupItem {
    constructor(
        public readonly title: string,
        public readonly subject: SponsorSubjectIdentity
    ) {}

    public toString(): string {
        return this.title;
    }
}

@Component({
    selector: 'sponsor-subject-component',
    template: require('./sponsor-subject.component.html'),
    styles: [require('./sponsor-subject.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        SponsorSelectedSubjectRouteData,
        SponsorSelectedSubjectLabResultsStorage,
        { provide: SUBJECT_LAB_TESTS_DATA_SOURCE, useExisting: SponsorSelectedSubjectLabResultsStorage },
    ]
})
export class SponsorSubjectComponent {
    public readonly selectedZcSubjectId$: Observable<string>;
    public readonly subjectLookupItems$: Observable<SubjectLookupItem[]>;
    public readonly selectedSubjectLookupItems$: Observable<SubjectLookupItem[]>;
    public readonly tabs$: Observable<SubjectTab[]>;

    public readonly subjectDataLegend$: Observable<GridLegendItem[]>;
    public readonly showOnlyFlagged = new FormControl();
    public readonly ctSubject$: Observable<Subject>;

    private showOnlyFlaggedSubscription: Subscription;
    private readonly selectedSubjectId$: Observable<string>;

    constructor(
        private urlBuilder: UrlBuilder,
        private readonly studyRouteData: SponsorRouteData,
        @Self() private selectedSubjectLabResultsStorage: SponsorSelectedSubjectLabResultsStorage,
        private readonly dataRevisionIndicator: DataRevisionIndicator,
        private readonly ctSubjectsStorage: SponsorCtSubjectsStorage,
        private readonly route: ActivatedRoute
    ) {
        this.selectedZcSubjectId$ = route.params.pipe(map(p => p[routeParams.zcSubjectId]));
        this.subjectLookupItems$ = this.createSubjectLookupItems();
        this.selectedSubjectLookupItems$ = this.getSelectedSubjectLookupItems(this.selectedZcSubjectId$, this.subjectLookupItems$);
        this.tabs$ = this.createTabs(studyRouteData.selectedStudy, this.selectedZcSubjectId$);

        this.selectedSubjectId$ = this.selectedSubjectLookupItems$.pipe(filter(si => !isEmpty(si)), map(si => si[0].subject.subjectId.toString()));

        this.showOnlyFlaggedSubscription = this.showOnlyFlagged.valueChanges.subscribe((x: boolean) => {
            this.selectedSubjectLabResultsStorage.showOnlyFlagged.next(x);
        });

        this.ctSubject$ = this.getCtSubject();
        this.subjectDataLegend$ = this.createLegendItems(this.ctSubject$);
    }

    public ngOnDestroy(): void {
        this.showOnlyFlaggedSubscription.unsubscribe();
    }

    public onSubjectChange(selectedItems: SubjectLookupItem[]) {
        if (!selectedItems.length) {
            return;
        }

        const study = this.studyRouteData.snapshot.selectedStudy;
        const subject = selectedItems[0].subject;

        this.urlBuilder
            .esa()
            .sponsor()
            .study(study)
            .subjects(subject.zcSubjectId)
            .navigate({ preserveFragments: true });
    }

    private getSubjectTitle(subject: SponsorSubjectIdentity): string {
        return SubjectTitleRenderer.renderTitle({
            data: {
                subjectNumber: subject.subjectNumber,
                initials: subject.subjectInitials,
                screeningNumber: subject.screeningNumber,
                zcSubjectId: subject.zcSubjectId,
            }
        });
    }

    private createSubjectLookupItems() {
        return this.route.data.pipe(
            map((data: {[routeData.sponsorSubjectIdentities]: SponsorSubjectIdentity[]}) => data[routeData.sponsorSubjectIdentities]),
            map(subjects =>
                chain(subjects)
                .map<SubjectLookupItem>(subject =>
                    new SubjectLookupItem(this.getSubjectTitle(subject), subject)
                )
                .sortBy(x => x.title)
                .value()
            ),
            publishReplay(1),
            refCount()
        );
    }

    private getSelectedSubjectLookupItems(
        selectedZcSubjectId$: Observable<string>,
        lookupItems$: Observable<SubjectLookupItem[]>
    ) {
        return combineLatest(
            selectedZcSubjectId$,
            lookupItems$
        ).pipe(
            map(([selectedZcSubjectId, lookupItems]) => {
                const selectedItem = lookupItems.find(item => item.subject.zcSubjectId === selectedZcSubjectId);

                return selectedItem
                    ? [selectedItem]
                    : [];
            }),
            publishReplay(1),
            refCount()
        );
    }

    private createTabs(study$: Observable<SponsorStudy>, zcSubjectId$: Observable<string>): Observable<SubjectTab[]> {
        return combineLatest(
            study$,
            zcSubjectId$
        ).pipe(
            map(([study, zcSubjectId]) =>
                [
                    { title: 'Table', url: this.createTabUrl(study, zcSubjectId, 'lab-tests-grid') },
                    { title: 'Chart', url: this.createTabUrl(study, zcSubjectId, 'lab-tests-chart') }
                ]
            )
        );
    }

    private createTabUrl(
        sponsorStudy: SponsorStudy,
        zcSubjectId: string,
        path: 'lab-tests-grid' | 'lab-tests-chart'
    ): any[] {
        return this.urlBuilder
            .esa()
            .sponsor()
            .study(sponsorStudy)
            .subjects(zcSubjectId, path)
            .url({ preserveFragments: true });
    }

    private createLegendItems(subject$: Observable<Subject>): Observable<GridLegendItem[]> {
        return subject$.pipe(
            map(ctSubject => {
                const subjectDataLegend: GridLegendItem[] = [];

                if (ctSubject && ctSubject.hasSubjectDataRevision) {
                    subjectDataLegend.push({
                        text: 'Δ', cssClass: 'revisioned-result', label: this.dataRevisionIndicator.subjectDataRevisionMessage
                    });
                }

                return subjectDataLegend;
            })
        );
    }

    private getCtSubject(): Observable<Subject> {
        return this.ctSubjectsStorage.loadCtSubject(
            this.studyRouteData.selectedStudy,
            this.selectedSubjectId$
        ).pipe(
            publishReplay(1),
            refCount()
        );
    }
}